import { type PlaceDetails } from 'GooglePlaces';

// Which Google Places address components populate which user address field, and in what order of
// preference. Ported verbatim from `fullAddressConfig` in the old edit_documents_dir.js: for `city`,
// if `locality` is absent we walk down the list until we find the highest level Google actually
// returned for this place.
const FULL_ADDRESS_CONFIG = {
    address_line_1: 'name',
    city: {
        locality: 'long',
        postal_town: 'long',
        sublocality_level_1: 'long',
        sublocality_level_2: 'long',
        sublocality_level_3: 'long',
        sublocality_level_4: 'long',
        sublocality_level_5: 'long',
    },
    state: {
        administrative_area_level_1: 'long',
        administrative_area_level_2: 'long',
        administrative_area_level_3: 'long',
        administrative_area_level_4: 'long',
        administrative_area_level_5: 'long',
    },
    zip: {
        postal_code: 'short',
    },
    country: {
        country: 'short',
    },
} as const;

type AddressField = keyof typeof FULL_ADDRESS_CONFIG;

// `placeDetails` keys beyond those in GooglePlaces' PlaceDetails type (e.g. postal_town and the
// higher sublocality levels) are still present at runtime — attachGooglePlacesToInput copies every
// address component across — so this reads through an index signature.
type LooseShortLong = { short?: string; long?: string };
type LoosePlaceDetails = PlaceDetails & Record<string, LooseShortLong | string | number | undefined>;

function formatPartialAddress(field: AddressField, placeDetails: LoosePlaceDetails): string {
    const config = FULL_ADDRESS_CONFIG[field];

    if (typeof config === 'string') {
        const value = placeDetails[config];
        return typeof value === 'string' ? value : '';
    }

    // Take the first configured key this place actually has a value for.
    for (const [placeDetailsKey, lengthType] of Object.entries(config)) {
        const component = placeDetails[placeDetailsKey];
        if (component && typeof component === 'object') {
            const value = component[lengthType as 'short' | 'long'];
            if (value) return value;
        }
    }

    return '';
}

// Maps a chosen Google Place onto the mailing-address form fields.
export function addressFromPlaceDetails(placeDetails: PlaceDetails): Record<AddressField, string> {
    const loose = placeDetails as LoosePlaceDetails;

    return {
        address_line_1: formatPartialAddress('address_line_1', loose),
        city: formatPartialAddress('city', loose),
        state: formatPartialAddress('state', loose),
        zip: formatPartialAddress('zip', loose),
        country: formatPartialAddress('country', loose),
    };
}
